import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {Observable} from 'rxjs';
import {UserService} from "./user.service";
import {AuthenticationService} from "./authentication.service";
import {isCentralRole, Roles} from "./x-enums/roles";
import {AppRoutes} from "../enums/routes";
import {KeyboardService} from "../_services/keyboard.service";
import {isAdditiveOnlyGate, isSoleDestination} from "./context/context-view-map";
import {declaresContextEntryRefusal} from "./context/context-entry-refusal";
import {EMPLOYMENT_PENDING_MAY_VISIT, lockoutRouteForSession} from "./gate-lockout-route";

@Injectable({
  providedIn: 'root'
})
export class AuthGuard {
  constructor(
    private router: Router,
    private userService: UserService,
    private authenticationService: AuthenticationService,
    private keyboardService: KeyboardService
  ) {
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {

    if (this.keyboardService.isOptionPressed) {
      window.open(state.url, '_blank');
      this.keyboardService.isOptionPressed = false;
      return false;
    }

    const user = this.authenticationService.userValue;
    if (user) {
      const {roles} = route.data;
      const {deactivated} = route.data;

      // Account-security gate (#282): a session that proved a password but not the second factor its company
      // requires may reach the remediation screen and NOTHING else. Ahead of every branch below — including the
      // context chooser — because each of those sends the session somewhere it is not yet allowed to be, and a
      // second factor that can be skipped by navigating to a differently-shaped route is not a second factor.
      //
      // NOT confined to employees, unlike the forms gate further down: `AccountSecurityLevel` is a company-wide
      // policy and restricts a ShardAdmin, a CompanyOwner and a PlatformOwner identically. That is also why the
      // remediation route lives outside every panel shell (see AppRoutes.TwoFactorRequired).
      //
      // Inert for every session that predates the claim: `accountSecurity` is absent there and reads as "ok".
      //
      // Read directly rather than through `lockoutRouteForSession` (which the employee-gate block below uses)
      // because this branch has to run HERE, above the context-chooser exemption and the shell redirects,
      // while those gates run below them — the shared rule states the *order* of the three gates, not where in
      // this method each is evaluated. It is the same first answer either way: two-factor outranks both.
      const onTwoFactorRequired = state.url.split('?')[0].startsWith('/' + AppRoutes.TwoFactorRequired);
      if (this.authenticationService.isAccountSecurityRestricted) {
        if (onTwoFactorRequired) return true;
        this.router.navigate(['/' + AppRoutes.TwoFactorRequired]).then();
        return false;
      }
      // A REFUSED CONTEXT ENTRY (#279) reaches the remediation screen outright — the one way to need enrolment
      // while carrying no claim that says so.
      //
      // `ContextSessionService` refuses such an entry and mints nothing, deliberately: that session may still
      // enter a company that requires nothing, and restricting it would lock the account out of every other
      // tenant it holds a grant in. So `isAccountSecurityRestricted` reads FALSE while enrolment is exactly what
      // the person needs, and every branch below — the mirror bounce, and the "an employee belongs in its
      // panel" redirect that catches a session refused while SWITCHING out of a tenant — threw them back into
      // the shell they had just failed to fill (sign in, pick a tile, land nowhere: the reported defect).
      //
      // Placed exactly like the chooser exemption below, and for the same reason: those redirects each send a
      // session somewhere its *current* roles belong, and this one has business at one specific route first. It
      // opens THAT route and nothing else, and the claim still outranks the parameter in the direction that
      // matters — a genuinely restricted session is pinned here by the branch above whatever the URL says. So a
      // forged parameter buys a screen that grants nothing, which is what makes it safe to read here.
      if (onTwoFactorRequired && declaresContextEntryRefusal(state.url)) return true;

      // The mirror bounce: standing on the remediation screen with nothing left to remediate. Sent to the panel
      // an employee belongs in rather than the dashboard, whose data calls an employee session 403s on — the
      // same distinction the forms-gate bounce below draws, for the same reason.
      if (onTwoFactorRequired) {
        const home = user.permissions.includes(Roles.Employee) ? '/' + AppRoutes.EmployeePanel : '/';
        this.router.navigate([home]).then();
        return false;
      }

      // The context chooser (#279) is reachable by EVERY signed-in session, ahead of every branch below.
      // Each of those branches redirects a session somewhere its *current* roles belong — the platform landing,
      // the profile, the employee list, the panels — and the chooser exists precisely because those roles have
      // not been settled yet. Without this the page would be unreachable by the sessions that need it most: a
      // companyless platform owner is bounced to /owner/companies, and a bracketing-only identity to its profile.
      if (state.url.split('?')[0].startsWith('/' + AppRoutes.AuthenticationChooseContext)) return true;

      // Company/shard-independent platform owners (e.g. signed in via Entra) may only reach platform-level
      // routes. Any company/shard-scoped route (one that does not explicitly require PlatformOwner) is
      // redirected to the platform landing — except general account pages (e.g. the profile) which are always
      // available.
      if (this.authenticationService.isCompanyless && user.permissions.includes(Roles.PlatformOwner)) {
        const generalRoutes = ['/' + AppRoutes.AuthenticationProfile];
        const isGeneralRoute = generalRoutes.some(r => state.url.split('?')[0].startsWith(r));
        if (isGeneralRoute) return true;
        // Platform-level capabilities a company-less principal (e.g. an Entra sign-in) can exercise without a
        // company context: PlatformOwner routes, plus platform-wide additive roles they actually hold (e.g.
        // Auditor — the audit trail is platform-wide). Company/shard-scoped routes stay redirected to the landing.
        const platformRoles: string[] = [Roles.PlatformOwner, Roles.Auditor];
        const reachable = !!roles && roles.some((r: string) => platformRoles.includes(r) && user.permissions.includes(r));
        if (!reachable) {
          this.router.navigate(['/' + AppRoutes.OwnerCompanies]).then();
          return false;
        }
        return true;
      }
      // A bracketing-only external session — signed in as the identity but carrying no role and no company yet
      // (an invited payroll accountant who hasn't redeemed, or any identity awaiting an account switch) — has
      // nothing to do in the app but manage its account. Land it on the profile (where it redeems an invitation
      // and reaches the account switcher) instead of the dashboard, whose data call 403s and would trip the error
      // interceptor's logout. Platform owners hold the PlatformOwner permission, so they never reach this branch.
      if (this.authenticationService.isExternalSession
        && this.authenticationService.isCompanyless
        && user.permissions.length === 0) {
        const path = state.url.split('?')[0];
        if (path.startsWith('/' + AppRoutes.AuthenticationProfile)) return true;
        this.router.navigate(['/' + AppRoutes.AuthenticationProfile]).then();
        return false;
      }
      // External-only payroll accountants ("Lohnbuchhalter") get a deliberately narrow company view: only the
      // employee list/detail, employment relationships and general account pages (e.g. their profile). Allowed
      // routes opt in with `data.payroll === true`; every other route is bounced to the employee list.
      //
      // It confines the session only while `Payroll` is its ONLY destination role — `isSoleDestination`, the
      // shared rule this branch and the two operator ones below all read from, mirroring
      // `ContextViewMap.IsDestination`. This used to be a hand-written exclusion list
      // (`!ShardAdmin && !PlatformOwner`), correct while a payroll accountant could only be an external account
      // holding exactly one role; scoped grants (#261/#262) made every combination reachable, and the list was
      // never revisited. A `Payroll + ShiftLead` session — both roles open the Management view, so both ride in
      // one token — was shown the Dienstplan entry (#280) and then bounced off the screen by this very branch,
      // and a `CompanyOwner` who also held `Payroll` was confined to the employee list of the company they own.
      // Additive roles (Auditor, SensitiveDataAccessor) deliberately do NOT count as another destination: an
      // accountant permitted unmasked data is still an accountant, and letting that extra privilege switch the
      // confinement off would be the one direction that matters here.
      //
      // They do, however, open their OWN surface once the session is confined — see
      // `opensGrantedAdditiveSurface` below. That is the later, smaller question: not "is this still a payroll
      // session" (it is), but "may it reach the one screen the extra role it was granted exists for".
      if (isSoleDestination(user.permissions, Roles.Payroll)) {
        const path = state.url.split('?')[0];
        const generalRoutes = ['/' + AppRoutes.AuthenticationProfile];
        if (generalRoutes.some(r => path.startsWith(r))) return true;
        if (route.data['payroll'] === true) return true;
        if (this.opensGrantedAdditiveSurface(roles, user.permissions)) return true;
        this.router.navigate(['/' + AppRoutes.CompanyEmployee]).then();
        return false;
      }
      // External-only tax advisers (`Roles.Steuerberater`) get the same deliberately narrow company view: the
      // fiscal record of the registers, and general account pages. Allowed routes opt in with
      // `data.fiscal === true`; every other route is bounced to the TSE overview.
      //
      // #767 left this branch UNWRITTEN on purpose and said so at length: a confinement is two halves — routes
      // that opt in, and a home to bounce everything else to — and the role had no route at all, so writing it
      // then would have bounced every navigation to something unregistered. #769 registers the first fiscal
      // route (`/legal/tss`, `data.fiscal: true`), so the branch lands here with it, in exactly the shape that
      // comment specified. Stories #770/#771 flag their own screens with the same key; there is no list here to
      // keep in step, which is the point of a flag rather than a route table.
      //
      // `isSoleDestination` for the same reason the branch above uses it, and it is load-bearing for this role
      // in particular: a small firm's adviser routinely also holds `Payroll` for the same company, and both
      // roles open `ContextView.Management`, so both ride in ONE token. That session has two destinations, so
      // NEITHER branch confines it — it stays in the shell the two roles add up to. Confining it by whichever
      // branch happened to run first is precisely the #280 defect, avoided by construction.
      //
      // The bounce target is the TSE overview rather than the dashboard, mirroring the employee list above: the
      // dashboard's own data calls are refused for a session holding only this role, so landing there is the
      // "sign in, land nowhere" shape. The confinement is still only a navigation affordance — the boundary is
      // server-side (`TSSController`'s gate), and `roles.every(...)` at the bottom of this method already
      // refuses this session every management screen even if this branch were removed.
      if (isSoleDestination(user.permissions, Roles.Steuerberater)) {
        const path = state.url.split('?')[0];
        const generalRoutes = ['/' + AppRoutes.AuthenticationProfile];
        if (generalRoutes.some(r => path.startsWith(r))) return true;
        if (route.data['fiscal'] === true) return true;
        if (this.opensGrantedAdditiveSurface(roles, user.permissions)) return true;
        this.router.navigate(['/' + AppRoutes.LegalTse]).then();
        return false;
      }
      //
      // The employee-only gates: required forms (#404) and required contact channels (#558). A 'restricted'
      // employee may only reach the one remediation screen its gate names; everything else in the app is
      // redirected there. Conversely, an employee who is no longer restricted is bounced off such a screen
      // back into the panel. (Non-employees never carry either restricting claim.)
      //
      // WHICH screen is owed is not decided here — `lockoutRouteForSession` owns that, and the error
      // interceptor and `GateRefreshService` read the same rule. A session restricted by BOTH gates therefore
      // resolves them in one fixed order (two-factor → forms → contact): satisfying the forms gate re-issues a
      // token that still says `contact_gate = restricted`, and the next navigation pins it here again, to the
      // contact screen. No "what is next" logic lives in this guard, and none should.
      if (user.permissions.includes(Roles.Employee)) {
        const path = state.url.split('?')[0];
        const required = lockoutRouteForSession(this.authenticationService);

        // An employment-pending session (#864) may also stand on the forms or contact screen: those are the
        // things it is meant to do before the start date, and the server opens them to it. The per-screen
        // bounce below still sends it away when there is nothing to do there.
        const visitingWhilePending = required === AppRoutes.EmploymentPending
          && EMPLOYMENT_PENDING_MAY_VISIT.some(r => path.startsWith('/' + r));

        // Pin. `startsWith` rather than equality, mirroring every other route check in this method — these
        // screens take no parameters today and must not start missing if one is ever added.
        if (required && !visitingWhilePending && !path.startsWith('/' + required)) {
          this.router.navigate(['/' + required]).then();
          return false;
        }

        // The bounce, per screen. A `review` session may legitimately BE on either screen: a Grace-backfilled
        // form (#404) or a contact re-verification inside its window (#558) does not block yet but IS dealt
        // with there — bouncing it would make the whole non-blocking branch, and its "Später erledigen"
        // button, unreachable dead code. Only a session with no business there at all is sent back. A plain
        // review flag with nothing actually pending is bounced by the screen's own refresh() instead, which
        // has the live gate status these token claims lack.
        if (path.startsWith('/' + AppRoutes.FormsRequired)
          && !this.authenticationService.isFormsRestricted && !this.authenticationService.isFormsReviewDue) {
          this.router.navigate(['/' + AppRoutes.EmployeePanel]).then();
          return false;
        }
        if (path.startsWith('/' + AppRoutes.ContactRequired)
          && !this.authenticationService.isContactRestricted && !this.authenticationService.isContactReviewDue) {
          this.router.navigate(['/' + AppRoutes.EmployeePanel]).then();
          return false;
        }
        if (path.startsWith('/' + AppRoutes.EmploymentPending) && !this.authenticationService.isEmploymentPending) {
          this.router.navigate(['/' + AppRoutes.EmployeePanel]).then();
          return false;
        }
      }

      if (deactivated && deactivated == true) {
        if (user.permissions.includes(Roles.Employee)) {
          this.authenticationService.logout();
        } else {
          this.router.navigate(['/']).then();
        }
        return false;
      }

      if (
        (roles && !roles.includes(Roles.Employee) && user.permissions.includes(Roles.Employee)) ||
        (!roles && user.permissions.includes(Roles.Employee))
      ) {
        this.router.navigate(['/' + AppRoutes.EmployeePanel]).then();
        return false;
      }
      if (roles && roles.includes(Roles.Employee) && !user.permissions.includes(Roles.Employee)) {
        this.router.navigate(['/']).then();
        return false;
      }

      // Operator (#205): a standalone baseline role held INSTEAD of ShardAdmin/Employee — mirrors the Employee
      // redirect above exactly. Any route that does not explicitly grant Operator bounces it to its own panel;
      // it never falls through to the admin fallback below, whose data calls it has no permissions for.
      //
      // …but only when `Operator` is the session's ONLY destination role. Creating an Operator+Admin account is
      // rejected by both the create-user form and `UserService` (Operator is held INSTEAD of a management
      // baseline), so this is defence in depth — it covers an account that predates that rule, was assigned a
      // role row directly, or reached the same shard through a scoped grant. It matters because the redirect
      // matches every route that does not list Operator, i.e. essentially the whole admin app: without the
      // exemption such an account would be locked out of everything but the operator panel while still carrying
      // that authority on the wire.
      //
      // Same rule as the Payroll branch above — deliberately the SAME CALL, not a second exclusion list. The
      // two lists had already drifted (this one excluded CompanyOwner, that one did not) and neither had heard
      // of `ShiftLead`, so an `Operator + ShiftLead` grant pair was swept into the operator panel.
      //
      // The additive-surface exemption applies here too, and for the same reason: `Auditor` is grantable at
      // shard scope beside `Operator` exactly as it is at company scope beside `Payroll`, so an
      // `Operator + Auditor` session is the identical shape. Fixing only the payroll half would leave the two
      // branches disagreeing again, which is the drift `isSoleDestination` was extracted to stop.
      const operatorOnly = isSoleDestination(user.permissions, Roles.Operator)
        && !this.opensGrantedAdditiveSurface(roles, user.permissions);
      if (
        (roles && !roles.includes(Roles.Operator) && operatorOnly) ||
        (!roles && operatorOnly)
      ) {
        this.router.navigate(['/' + AppRoutes.OperatorPanel]).then();
        return false;
      }
      // The reverse case: an Operator-EXCLUSIVE route (the operator panel itself) rejects everyone else,
      // mirroring the Employee reverse-check above. A route that lists Operator ALONGSIDE another destination
      // role (e.g. the open-tables overview's [ShardAdmin, CompanyOwner, PlatformOwner, Operator]) is
      // intentionally reachable by both and must NOT be narrowed here — otherwise a session holding one of
      // those other roles but not Operator would be bounced from a route it is also explicitly granted.
      //
      // Driven from the SAME rule as the two session-side branches above, applied to the route's declared roles
      // instead of the session's held ones: "is Operator the only thing here that confers a destination?" is
      // the identical question. Its hand-written exclusion list was wrong in the identical way, just latently —
      // no route ships today naming Operator beside a non-admin destination role. It would have bitten as soon
      // as one did, because this check sits ABOVE the `anyRoles` OR-list: a route gated [Operator, ShiftLead]
      // with `anyRoles: [ShiftLead]` would have refused the shift lead here, before the list naming them was
      // ever read. Legacy endpoint-permission ids mixed into `roles` are not destinations, so a route pairing
      // Operator with one of those stays exclusive, exactly as before.
      const operatorExclusive = isSoleDestination(roles, Roles.Operator);
      if (operatorExclusive && !user.permissions.includes(Roles.Operator)) {
        this.router.navigate(['/']).then();
        return false;
      }

      // `data.anyRoles`: an OR-list of CENTRAL roles, any one of which admits the route outright. Kept as its
      // own key rather than being folded into `data.roles`, because `roles` is AND-evaluated at the bottom of
      // this method over legacy endpoint-permission ids — a central role dropped in there would newly be
      // REQUIRED of every legacy granular session that reaches the route today, turning a widening into a
      // lockout. Fed from the same `PermissionSet` the navbar checks (`PermissionSet.routeData()`), so a
      // screen's menu entry and its route cannot drift apart.
      //
      // Evaluated before the role short-circuits below because those only know the four roles they name; a set
      // may be reachable by a role none of them mentions (e.g. `ShiftLead` on the shift plan, #280). It sits
      // after every "this session belongs in another shell" redirect above on purpose — an OR-role must widen
      // a route, never smuggle a session out of the app it chose.
      const anyRoles = route.data['anyRoles'] as string[] | undefined;
      if (anyRoles?.some((role: string) => user.permissions.includes(role))) return true;

      if (roles && roles.includes(Roles.PlatformOwner) && user.permissions.includes(Roles.PlatformOwner)) return true;
      // Central roles explicitly named in a route's `roles` array are OR'd — any one held role is enough to
      // pass. `roles.every(...)` below is only correct for legacy granular-permission-id arrays (where holding
      // every listed id is genuinely required); once a route lists more than one central role (e.g. the
      // open-tables overview's [ShardAdmin, CompanyOwner, PlatformOwner, Operator], #205) `.every()` would
      // wrongly require holding ALL of them simultaneously. ShardAdmin/Operator get their own explicit
      // short-circuits for that case — PlatformOwner already has one above.
      if (roles && roles.includes(Roles.ShardAdmin) && user.permissions.includes(Roles.ShardAdmin)) return true;
      // CompanyOwner needs the same short-circuit as its siblings, and lacking one was a real lockout: a
      // session holding ONLY CompanyOwner (which is how the role is held — it comes from a grant, and grants
      // hang off an ExternalIdentity) matched none of the checks above, fell through to `roles.every(...)`
      // and was redirected off every route listing more than one central role. That covered the two routes
      // that already do so — company/device-config and company/access — so the owner of a company could not
      // reach the screens meant for exactly them.
      if (roles && roles.includes(Roles.CompanyOwner) && user.permissions.includes(Roles.CompanyOwner)) return true;
      if (roles && roles.includes(Roles.Operator) && user.permissions.includes(Roles.Operator)) return true;
      // The ShardAdmin supersede: a shard admin passes a route gated on **legacy endpoint-permission ids**
      // without holding any of them. That is not a shortcut, it is the whole reason the branch exists — the
      // `Admin` meta role grants blanket authority server-side, so `CentralRoleMapper.BuildTokenRoleNames`
      // stamps such a session `["ShardAdmin"]` and nothing else, and `roles.every(...)` below would refuse it
      // every screen in the admin app. It is the route-side mirror of `PermissionSet._super` (literally
      // `"ShardAdmin"` on every set in `PermissionService`, and what the navbar checks); `routeData()` does not
      // carry `_super`, so the guard restates it here.
      //
      // It used to read "any route that does not name PlatformOwner" — written in the initial commit, when a
      // route's `roles` could only be legacy permission ids and `Owner`/`Admin` were the only central names in
      // play. Central roles later moved into those same arrays and the bypass could not tell the two
      // vocabularies apart, so it also admitted every route gated on a role a shard admin does not hold and the
      // route never asked for: `/audit`, gated on the additive `Roles.Auditor` alone, opened for every shard
      // admin, who then collected 403s from `AuditController` — which names PlatformOwner/CompanyOwner/Auditor
      // and no baseline role at all.
      //
      // So it now supersedes exactly what it was always meant to supersede. `/audit` is the only route whose
      // verdict moves: every other central-role gate is already decided above (ShardAdmin/CompanyOwner/Operator
      // short-circuits) or refused earlier (the Employee reverse-check for `ep_base`, the operator-exclusive
      // check for the operator panel, `roles.every` for the PlatformOwner-gated owner routes).
      if (roles && !roles.some((role: string) => isCentralRole(role))
        && user.permissions.includes(Roles.ShardAdmin)) return true;
      if (roles && !roles.every((x: string) => user.permissions.includes(x))) {
        this.router.navigate(['/']).then();
        return false;
      }
      return true;
    }

    this.router.navigate(['/login'], {queryParams: {returnUrl: state.url}}).then();
    return false;
  }

  /**
   * Whether a **confined** session may still reach this route because the route is gated entirely on additive
   * central roles (`Auditor`, `SensitiveDataAccessor`) that the session was actually granted.
   *
   * The confinement branches above exist because a payroll accountant, or an operator, has no business roaming
   * the admin app — and that stays true. But a role somebody was *deliberately granted* has to do something,
   * and an additive role's whole nature is that it comes beside a baseline rather than instead of one. `/audit`
   * is the case: company-scoped, gated on `[Roles.Auditor]`, admitted server-side by `AuditController` for
   * `Auditor` with no baseline role required, and offered in the sidebar by `navbar.service.ts` on `isAuditor`.
   * Without this an external accountant granted `Payroll + Auditor` sees the Audit entry and is bounced to the
   * employee list by clicking it — the same "the navbar offers it and the guard refuses it" shape #280 fixed
   * for the Dienstplan, and the extra role bought them nothing at all.
   *
   * **This does not undo `isSoleDestination`, and must not be made to.** That predicate answers whether the
   * confinement *applies*, and additive roles deliberately do not count there: an accountant permitted unmasked
   * data is still an accountant, and letting an extra privilege switch the whole confinement off is the one
   * direction that matters. This answers the later, smaller question of what a session that IS confined may
   * still reach, and it opens only the surface the extra role is for: `isAdditiveOnlyGate` refuses a route
   * naming any destination role or any legacy endpoint-permission id, and the session must hold every role
   * listed. A `Payroll`-only session holds no additive role, so nothing widens for it.
   *
   * @param roles The route's declared `data.roles`.
   * @param held The session's role claims (`user.permissions`).
   */
  private opensGrantedAdditiveSurface(roles: string[] | undefined, held: readonly string[]): boolean {
    return isAdditiveOnlyGate(roles) && roles!.every((role: string) => held.includes(role));
  }

}
